import cx from 'classnames';
import React, { useCallback } from 'react';
import { Modal as ModalComponent } from 'react-overlays';
import useBuildVariantProps from '../../../modules/theme/useBuildVariantProps';
import styles from './Modal.module.scss';
import type { ModalProps } from './Modal.types';

/**
 * Modal component for displaying a dialog window.
 */
const Modal = (props: ModalProps) => {
  const {
    size = 'Medium',
    onHide,
    show,
    children,
    header,
    footer,
    backdrop = true,
    renderBackdrop,
    disableBackdropDismiss,
    'component-variant': componentVariant,
    className,
    Transition,
  } = props;

  /**
   * Handles click events on the modal backdrop. Calls onHide if backdrop dismissal is enabled.
   * @returns {void}
   */
  const handleBackdropClick = useCallback(() => {
    if (!disableBackdropDismiss && onHide) {
      onHide();
    }
  }, [disableBackdropDismiss, onHide]);

  /**
   * Creates the backdrop element for the modal.
   * @returns {React.ReactElement} The backdrop element
   */
  const createBackdrop = useCallback(() => {
    if (renderBackdrop) {
      return <div onClick={handleBackdropClick}>{renderBackdrop}</div>;
    }
    return <div className={cx(styles.backdrop)} onClick={handleBackdropClick} />;
  }, [renderBackdrop, handleBackdropClick]);

  let headerComponent, footerComponent, content;

  if (header) {
    headerComponent = <div className={cx(styles.header)}>{header}</div>;
  }

  if (footer) {
    footerComponent = <div className={cx(styles.footer)}>{footer}</div>;
  }

  if (children) {
    content = <div className={cx(styles.content)}>{children}</div>;
  }

  const variantProps = useBuildVariantProps(
    'modal',
    { size },
    undefined,
    componentVariant,
  );

  return (
    <ModalComponent
      className={cx(styles.modal, styles[`modal--${size.toLowerCase()}`], className)}
      onHide={onHide}
      show={show}
      backdrop={backdrop}
      renderBackdrop={createBackdrop}
      container={document.body} // Ensure modal is appended to the document body for accessibility
      transition={Transition}
      {...variantProps}
    >
      <>
        {headerComponent}
        {content}
        {footerComponent}
      </>
    </ModalComponent>
  );
};

export default Modal;
